import { useState } from 'react'
import {
	Glyphs,
	MiniTabbedWidgetWrapper,
	WidgetInputComplexNumericField,
	WidgetLabel,
	WidgetSelectGroup,
	type WidgetSelectGroupItem,
} from '../../../../rapid-ui'
import type {
	Layer,
	SubstackAlignItems,
	SubstackChildSizing,
	SubstackDirection,
	SubstackJustifyContent,
	SubstackWidthSizing,
} from '../../data/v1'
import { BoxTabIcon, LayoutTabIcon, TextFormattingTabIcon } from '../../glyphs/tabs'
import { useHasNoWidthToFill, useSizeSetByParent } from '../../lib/parent-sizing'
import { useDesignerActions } from '../../use-designer-actions'
import { BoxInspector } from './box'
import { FloatControls } from './float-controls'
import { FontFamilyInspector } from './font-family'
import { SetByParent } from './set-by-parent'

const directionItems: WidgetSelectGroupItem<SubstackDirection>[] = [
	{ id: 'column', label: 'Column', render: () => <Glyphs.core.ArrangeColumnIcon /> },
	{ id: 'row', label: 'Row', render: () => <Glyphs.core.ArrangeRowIcon /> },
]

const rowAlignItems: WidgetSelectGroupItem<SubstackAlignItems>[] = [
	{ id: 'start', label: 'Top', render: () => <Glyphs.core.AlignYTopIcon /> },
	{ id: 'center', label: 'Middle', render: () => <Glyphs.core.AlignYCenterIcon /> },
	{ id: 'end', label: 'Bottom', render: () => <Glyphs.core.AlignYBottomIcon /> },
	{ id: 'stretch', label: 'Stretch', render: () => <Glyphs.core.AlignXStretchIcon /> },
]

const columnAlignItems: WidgetSelectGroupItem<SubstackAlignItems>[] = [
	{ id: 'start', label: 'Left', render: () => <Glyphs.core.AlignXLeftIcon /> },
	{ id: 'center', label: 'Center', render: () => <Glyphs.core.AlignXCenterIcon /> },
	{ id: 'end', label: 'Right', render: () => <Glyphs.core.AlignXRightIcon /> },
	{ id: 'stretch', label: 'Stretch', render: () => <Glyphs.core.AlignXStretchIcon /> },
]

const rowJustifyItems: WidgetSelectGroupItem<SubstackJustifyContent>[] = [
	{ id: 'start', label: 'Left', render: () => <Glyphs.core.AlignXLeftIcon /> },
	{ id: 'center', label: 'Center', render: () => <Glyphs.core.AlignXCenterIcon /> },
	{ id: 'end', label: 'Right', render: () => <Glyphs.core.AlignXRightIcon /> },
	{ id: 'space-between', label: 'Space between', render: () => <Glyphs.core.AlignXStretchIcon /> },
]

const columnJustifyItems: WidgetSelectGroupItem<SubstackJustifyContent>[] = [
	{ id: 'start', label: 'Top', render: () => <Glyphs.core.AlignYTopIcon /> },
	{ id: 'center', label: 'Middle', render: () => <Glyphs.core.AlignYCenterIcon /> },
	{ id: 'end', label: 'Bottom', render: () => <Glyphs.core.AlignYBottomIcon /> },
	{ id: 'space-between', label: 'Space between', render: () => <Glyphs.core.AlignYStretchIcon /> },
]

const childSizingItems: WidgetSelectGroupItem<SubstackChildSizing>[] = [
	{ id: 'auto', label: 'Fit content', render: () => <span className="text-xs">Auto</span> },
	{ id: 'equal', label: 'Equal split', render: () => <span className="text-xs">Equal</span> },
]

const widthSizingItems: WidgetSelectGroupItem<SubstackWidthSizing>[] = [
	{ id: 'auto', label: 'Fit content', render: () => <span className="text-xs">Auto</span> },
	{ id: 'fill', label: 'Fill parent', render: () => <span className="text-xs">Fill</span> },
]

export type SubstackLayerInspectorProps = {
	layer: Extract<Layer, { type: 'substack' }>
}

export function SubstackLayerInspector({ layer }: SubstackLayerInspectorProps) {
	const { updateLayer } = useDesignerActions()
	const [tab, setTab] = useState<'layout' | 'formatting' | 'box'>('layout')
	const sizeSetByParent = useSizeSetByParent(layer.id)
	const hasNoWidthToFill = useHasNoWidthToFill(layer.id) && !sizeSetByParent.width

	const alignItems = layer.direction === 'row' ? rowAlignItems : columnAlignItems
	const justifyItems = layer.direction === 'row' ? rowJustifyItems : columnJustifyItems

	return (
		<>
			<FloatControls layer={layer} />
			<MiniTabbedWidgetWrapper
				value={tab}
				onChange={setTab}
				tabs={[
					{
						id: 'layout',
						icon: <LayoutTabIcon />,
						label: 'Layout',
						children: (
							<div className="flex flex-col gap-2">
								{!layer.floating && (
									<SetByParent isSet={sizeSetByParent.width}>
										<WidgetLabel label="Width">
											<WidgetSelectGroup
												value={layer.widthSizing}
												onChange={(widthSizing) => updateLayer(layer.id, { widthSizing })}
												items={widthSizingItems}
											/>
										</WidgetLabel>
										{layer.widthSizing === 'fill' && hasNoWidthToFill && (
											<p className="text-[11px] text-grey-400">
												The parent row is only as wide as its content. Set it to Fill too.
											</p>
										)}
									</SetByParent>
								)}

								<WidgetLabel label="Direction">
									<WidgetSelectGroup
										value={layer.direction}
										onChange={(direction) => updateLayer(layer.id, { direction })}
										items={directionItems}
									/>
								</WidgetLabel>

								<WidgetLabel label="Align">
									<WidgetSelectGroup
										value={layer.alignItems}
										onChange={(alignItems) => updateLayer(layer.id, { alignItems })}
										items={alignItems}
									/>
								</WidgetLabel>

								<WidgetLabel label="Justify">
									<WidgetSelectGroup
										value={layer.justifyContent}
										onChange={(justifyContent) => updateLayer(layer.id, { justifyContent })}
										items={justifyItems}
									/>
								</WidgetLabel>

								<WidgetLabel label={layer.direction === 'row' ? 'Column widths' : 'Row heights'}>
									<WidgetSelectGroup
										value={layer.childSizing}
										onChange={(childSizing) => updateLayer(layer.id, { childSizing })}
										items={childSizingItems}
									/>
								</WidgetLabel>

								<WidgetLabel label="Gap">
									<WidgetInputComplexNumericField
										value={layer.gap}
										onChange={(gap) => updateLayer(layer.id, { gap })}
										min={0}
										suffix="px"
									/>
								</WidgetLabel>
							</div>
						),
					},
					{
						id: 'formatting',
						icon: <TextFormattingTabIcon />,
						label: 'Font',
						children: (
							<div className="flex flex-col gap-2">
								<FontFamilyInspector
									mode={layer.fontFamilyMode}
									provider={layer.fontFamilyProvider}
									googleFamily={layer.fontFamilyGoogleFamily}
									customUrl={layer.fontFamilyCustomUrl}
									customName={layer.fontFamilyCustomName}
									onModeChange={(fontFamilyMode) => updateLayer(layer.id, { fontFamilyMode })}
									onProviderChange={(fontFamilyProvider) =>
										updateLayer(layer.id, { fontFamilyProvider })}
									onGoogleFamilyChange={(fontFamilyGoogleFamily) =>
										updateLayer(layer.id, { fontFamilyGoogleFamily })}
									onCustomFontChange={(fontFamilyCustomUrl, fontFamilyCustomName) =>
										updateLayer(layer.id, { fontFamilyCustomUrl, fontFamilyCustomName })}
								/>
							</div>
						),
					},
					{
						id: 'box',
						icon: <BoxTabIcon />,
						label: 'Box',
						children: (
							<BoxInspector
								layerId={layer.id}
								background={layer.background}
								onBackgroundChange={(updates, options) =>
									updateLayer(layer.id, (prev) => ({
										background: { ...(prev as typeof layer).background, ...updates },
									}), options)}
								borderRadius={layer.borderRadius}
								onBorderRadiusChange={(borderRadius) => updateLayer(layer.id, { borderRadius })}
								borderRadiusCorners={layer.borderRadiusCorners}
								onBorderRadiusCornersChange={(borderRadiusCorners) =>
									updateLayer(layer.id, { borderRadiusCorners })}
								spacing={layer.spacing}
								onSpacingChange={(spacing) => updateLayer(layer.id, { spacing })}
								onTransientSpacingChange={(spacing) =>
									updateLayer(layer.id, { spacing }, { transient: true })}
							/>
						),
					},
				]}
			/>
		</>
	)
}
